<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { nextTick } from 'vue'; // Import nextTick for notification
import { API_BASE_URL } from '@/config';

// Reactive state for the list of safety items
const safeties = ref([]);
// Reactive state for the new safety item form input
const newSafety = ref({ safety_no: '', safety_desc: '' });
// Reactive state to hold the safety item currently being edited (null if not editing)
const editingSafety = ref(null);

// Reactive state to track the row clicked in the table for Edit/Delete actions
const selectedSafety = ref(null);


// !! IMPORTANT: Replace with the actual base URL of your Node.js backend !!
// This URL should match where your backend is running and where the /api/safeties routes are mounted.
const API_SAFETY_URL = '${API_BASE_URL}/api/safeties'; // <-- VERIFY THIS URL EXACTLY


const loading = ref(false); // Loading state for async operations (e.g., fetching, saving, deleting)
const error = ref(null); // Error state for displaying persistent error messages
// State for managing temporary notification messages (success, error, warning)
const notification = ref({
  show: false,
  message: '',
  type: '' // 'success', 'error', 'warning'
});

// --- Computed Properties for v-model Binding ---
// This computed property handles the binding for the Safety Number input field.
// It switches between the 'editingSafety' and 'newSafety' objects based on whether editing is active.
const currentSafetyNo = computed({
  get() {
    // If editing, get the safety_no from the editing object, otherwise from the new object
    return editingSafety.value ? editingSafety.value.safety_no : newSafety.value.safety_no;
  },
  set(value) {
    // If editing, update the safety_no in the editing object, otherwise in the new object
    if (editingSafety.value) {
      editingSafety.value.safety_no = value;
    } else {
      newSafety.value.safety_no = value;
    }
  }
});

// This computed property handles the binding for the Safety Description input field.
// It switches between the 'editingSafety' and 'newSafety' objects based on whether editing is active.
const currentSafetyDesc = computed({
  get() {
     // If editing, get the safety_desc from the editing object, otherwise from the new object
    return editingSafety.value ? editingSafety.value.safety_desc : newSafety.value.safety_desc;
  },
  set(value) {
    if (editingSafety.value) {
      editingSafety.value.safety_desc = value;
    } else {
      newSafety.value.safety_desc = value;
    }
  }
});

// --- End Computed Properties ---


// --- CRUD Operations ---

// Fetches all safety items from the backend API
const fetchSafeties = async () => {
  loading.value = true; // Set loading state to true
  error.value = null; // Clear any previous errors
  selectedSafety.value = null; // Deselect any selected item when refreshing the list
  try {
    // Send GET request to the API endpoint
    const response = await axios.get(API_SAFETY_URL);
    // Update the safeties ref with the data received from the backend
    safeties.value = response.data;
  } catch (err) {
    console.error('Error fetching safety items:', err);
    error.value = 'Failed to fetch safety items. Please try again later.';
    showNotification(error.value, 'error'); // Show an error notification
  } finally {
    loading.value = false; // Set loading state to false regardless of success or failure
  }
};

// Creates a new safety item by sending data to the backend API
const createSafety = async () => {
  // Basic client-side validation: check if required fields are not empty
  if (!newSafety.value.safety_no.trim() || !newSafety.value.safety_desc.trim()) {
    showNotification('Safety Number and Description are required.', 'warning');
    return; // Stop the function if validation fails
  }
  loading.value = true; // Set loading state
  error.value = null; // Clear previous errors
  try {
    // Send POST request to the API endpoint with the new safety data
    const response = await axios.post(API_SAFETY_URL, newSafety.value);

    // --- DEBUGGING LOG ---
    console.log('Backend response data for new safety item:', response.data);
    // --- END DEBUGGING LOG ---

    // Add the newly created item (returned from the backend) to the local list
    // This should trigger Vue to update the table display
    safeties.value.push(response.data);
    // Clear the form inputs
    newSafety.value = { safety_no: '', safety_desc: '' };
    showNotification('Safety item created successfully!', 'success'); // Show success notification
  } catch (err) {
    console.error('Error creating safety item:', err);
    // Handle specific error responses from the backend
    if (err.response?.status === 409) {
      error.value = 'Safety Number already exists.';
    } else {
      error.value = 'Failed to create safety item.';
    }
    showNotification(error.value, 'error'); // Show error notification
  } finally {
    loading.value = false; // Set loading state to false
  }
};

// Updates an existing safety item by sending data to the backend API
const updateSafety = async () => {
   // Ensure we are in editing mode and a safety item is selected
   if (!editingSafety.value || !selectedSafety.value) {
       showNotification('No safety item selected for editing.', 'warning');
       return;
   }
   // Basic client-side validation for the description (the only updateable field)
  if (!editingSafety.value.safety_desc.trim()) {
    showNotification('Safety Description is required.', 'warning');
    return;
  }

  loading.value = true; // Set loading state
  error.value = null; // Clear previous errors
  try {
    // Create a payload with only the updateable field(s)
    const payload = { safety_desc: editingSafety.value.safety_desc };
    // Send PUT request to the specific item endpoint using the safety_no from the selected item
    // Uses JavaScript template literals (backticks `` and ${}) to build the URL dynamically
    const response = await axios.put(`${API_SAFETY_URL}/${selectedSafety.value.safety_no}`, payload); // <-- This line builds the correct URL

    // Find the index of the updated item in the local list using its safety_no
    const index = safeties.value.findIndex(item => item.safety_no === selectedSafety.value.safety_no); // <-- Use safety_no for finding index
    if (index !== -1) {
      // Update the item in the local list with the data returned from the backend
      safeties.value[index] = { ...safeties.value[index], ...response.data };
    }
     // If the updated item was the one currently selected, update the selected item ref as well
     if (selectedSafety.value && selectedSafety.value.safety_no === selectedSafety.value.safety_no) { // This condition is always true, could just update directly
         selectedSafety.value = { ...selectedSafety.value, ...response.data }; // <-- Update selected item
     }

    editingSafety.value = null; // Exit editing mode
    showNotification('Safety item updated successfully!', 'success'); // Show success notification
  } catch (err) {
    console.error('Error updating safety item:', err);
    // Handle specific error responses from the backend
    if (err.response?.status === 404) {
      error.value = 'Safety item not found.';
    } else if (err.response?.status === 400) {
            // Handle specific backend validation errors like description missing
            error.value = err.response.data || 'Bad Request: Could not update safety item.';
        }
    else {
      error.value = 'Failed to update safety item.';
    }
    showNotification(error.value, 'error'); // Show error notification
  } finally {
    loading.value = false; // Set loading state to false
  }
};

// Deletes a safety item by sending a DELETE request to the backend API
const deleteSafety = async () => {
    // Ensure a safety item is selected for deletion
    if (!selectedSafety.value) {
        showNotification('Please select a safety item to delete.', 'warning');
        return;
    }
  // Get the safety_no of the item to delete from the selected item
  const safetyNoToDelete = selectedSafety.value.safety_no; // Get the number from the selected item

  // Show a confirmation prompt to the user
  if (confirm(`Are you sure you want to delete safety item ${safetyNoToDelete}? This may also delete associated records.`)) {
    loading.value = true; // Set loading state
    error.value = null; // Clear previous errors
    try {
      // Send DELETE request to the specific item endpoint using the safety_no
      // Uses JavaScript template literals (backticks `` and ${}) to build the URL dynamically
      await axios.delete(`${API_SAFETY_URL}/${safetyNoToDelete}`); // <-- This line builds the correct URL
      // Remove the deleted item from the local list by filtering
      safeties.value = safeties.value.filter(item => item.safety_no !== safetyNoToDelete);
      // Clear selected and editing states if they match the deleted item
      selectedSafety.value = null;
      if(editingSafety.value && editingSafety.value.safety_no === safetyNoToDelete) {
           editingSafety.value = null;
      }
      showNotification('Safety item deleted successfully!', 'success'); // Show success notification
    } catch (err) {
      console.error('Error deleting safety item:', err);
      // Handle specific error responses from the backend
      if (err.response?.status === 404) {
        error.value = 'Safety item not found.';
      } else if (err.response?.status === 409) {
           // Specific error message from the backend about Foreign Key conflicts
           error.value = err.response.data || 'Conflict: Cannot delete safety item because it is referenced by other entities.'; // Use backend message or a default
      }
      else {
        error.value = 'Failed to delete safety item.';
      }
      showNotification(error.value, 'error'); // Show error notification
    } finally {
      loading.value = false; // Set loading state to false
    }
  }
};

// --- UI Actions ---

// Selects a row in the table when clicked
const selectSafety = (item) => {
  selectedSafety.value = item;
    // If you select an item while currently editing another, cancel the editing
    if (editingSafety.value) {
        cancelEditing();
    }
    error.value = null; // Clear previous errors when selecting a new row
}

// Initiates the editing process for the currently selected safety item
const startEditing = () => {
  if (selectedSafety.value) {
       // Copy selected item data to the editing state to populate the form
       editingSafety.value = { ...selectedSafety.value };
       // Clear the new safety item form when starting edit mode
       newSafety.value = { safety_no: '', safety_desc: '' };
       error.value = null; // Clear any previous errors
  } else {
       showNotification('Please select a safety item to edit.', 'warning'); // Prompt user if no item is selected
  }
};

// Cancels the current editing process and clears the editing state
const cancelEditing = () => {
  editingSafety.value = null; // Clear the editing state
  // Clear the new safety item form (as it shares inputs via computed properties)
  newSafety.value = { safety_no: '', safety_desc: '' };
  error.value = null; // Clear any previous errors
};

// Resets the form to the state for adding a new safety item
 const addNew = () => {
     editingSafety.value = null; // Ensure not in editing mode
     selectedSafety.value = null; // Deselect any row in the table
     // Clear form for new entry
     newSafety.value = { safety_no: '', safety_desc: '' };
     error.value = null; // Clear any previous errors
 };

// Helper function to show a temporary notification message
const showNotification = (message, type) => {
  notification.value.message = message;
  notification.value.type = type;
  notification.value.show = true;

   // Use nextTick to ensure the notification element is in the DOM before trying to animate
   nextTick(() => {
        const notificationElement = document.querySelector('.notification');
        if (notificationElement) {
             // Ensure fade-out class is removed before reapplying to allow animation restart
             notificationElement.classList.remove('fade-out');
             // Trigger a reflow to ensure the class removal is registered by the browser
             void notificationElement.offsetWidth;
             // Add fade-out class after a delay to start the fade animation
            setTimeout(() => {
                     notificationElement.classList.add('fade-out');
                     // Hide completely after fade-out transition ends
                     notificationElement.addEventListener('transitionend', () => {
                          // Only hide if the element still has the fade-out class (prevents issues if state changes rapidly)
                          if (!notificationElement.classList.contains('fade-out')) return;
                           notification.value.show = false;
                     }, { once: true }); // Remove the event listener after it triggers once
                }, 5000); // Start fade-out after 5 seconds
            } else {
                 // Fallback to just hiding after a total time if the element wasn't found (shouldn't happen if show is true)
                 setTimeout(() => {
                     notification.value.show = false;
                 }, 5500); // Total time including implied fade duration
            }
       });
    };

// --- Lifecycle Hook ---
// Fetch safety items from the backend when the component is first mounted to the DOM
onMounted(fetchSafeties);
</script>

<template>
  <div class="safety-page"> <div class="container">
      <div class="form-panel">
        <h2>{{ editingSafety ? 'Edit Safety Item' : 'Add New Safety Item' }}</h2>
        <form @submit.prevent="editingSafety ? updateSafety() : createSafety()">
          <div class="form-group">
            <label for="safety_no">Safety Number:</label>
              <input type="text" id="safety_no" v-model="currentSafetyNo" required
                       :disabled="editingSafety !== null || loading">
           </div>
          <div class="form-group">
            <label for="safety_desc">Safety Description:</label>
               <input type="text" id="safety_desc" v-model="currentSafetyDesc" required
                       :disabled="loading">
           </div>

          <div class="form-actions">
            <button type="submit" class="save-button" :disabled="loading">
              {{ editingSafety ? 'Update Item' : 'Save Item' }}
            </button>
            <button type="button" class="cancel-button" @click="cancelEditing" v-if="editingSafety" :disabled="loading">
              Cancel
            </button>
          </div>
        </form>
      </div>

      <div class="table-panel">
        <h2>Safety Item List</h2>

          <div v-if="loading && safeties.length === 0">Loading safety items...</div>
          <div v-if="error && safeties.length === 0" class="error-message">{{ error }}</div>

        <div class="table-actions">
            <button @click="addNew" :disabled="loading">Add New</button>
            <button @click="startEditing" :disabled="!selectedSafety || loading">Edit</button>
            <button @click="deleteSafety" :disabled="!selectedSafety || loading">Delete</button>
            <button @click="fetchSafeties" :disabled="loading">Refresh</button>
          </div>

        <table class="site-table"> <thead>
            <tr>
              <th>Safety No</th>
              <th>Safety Description</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in safeties" :key="item.safety_no"
                :class="{ 'selected': selectedSafety && selectedSafety.safety_no === item.safety_no }"
                @click="selectSafety(item)">
                 <td>{{ item.safety_no }}</td>
               <td>{{ item.safety_desc }}</td>
              </tr>
               <tr v-if="safeties.length === 0 && !loading">
                  <td colspan="2" style="text-align: center;">No safety items found.</td>
               </tr>
          </tbody>
        </table>
      </div>
    </div>

      <div v-if="notification.show" :class="['notification', notification.type, { 'fade-out': !notification.show }]">
      {{ notification.message }}.
      </div>

  </div>
</template>

<style scoped>
/* Styles based on previous views for consistency and professional look */
.safety-page { /* Unique class name for this page */
    padding: 20px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background-color: #f4f6f8;
    min-height: calc(100vh - 40px); /* Ensure page takes at least full height */
    color: #333; /* Default text color */
}

.container {
    display: flex;
    gap: 20px;
    max-width: 1200px;
    margin: 0 auto;
    flex-wrap: wrap;
    align-items: flex-start; /* Align items to the top */
}

.form-panel, .table-panel {
    background-color: white;
    padding: 25px;
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    min-width: 300px;
     box-sizing: border-box; /* Include padding and border in the element's total width and height */
}

.form-panel {
     flex: 1 1 350px; /* Flex-grow, Flex-shrink, Basis */
     max-width: 400px; /* Optional: Limit max width for form */
}

.table-panel {
     flex: 2 1 500px; /* Flex-grow, Flex-shrink, Basis */
    overflow-x: auto;
}


h2 {
    color: #333;
    margin-top: 0;
    margin-bottom: 25px;
    border-bottom: 1px solid #eee;
    padding-bottom: 15px;
    font-size: 1.5rem;
    font-weight: 600; /* Slightly bolder heading */
}

.form-group {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-group label {
    display: block;
    color: #555;
    font-weight: bold;
    font-size: 0.95rem;
}

input[type="text"],
input[type="number"],
select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-sizing: border-box;
    font-size: 1rem;
    transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

input[type="text"]:focus,
input[type="number"]:focus,
select:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
}

input[type="text"]:disabled,
input[type="number"]:disabled,
select:disabled {
    background-color: #e9ecef;
    cursor: not-allowed;
    opacity: 0.8;
}

.form-actions {
    margin-top: 30px;
    display: flex;
    gap: 10px;
     flex-wrap: wrap; /* Allow buttons to wrap on small screens */
}

.save-button,
.cancel-button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    transition: background-color 0.3s ease, opacity 0.3s ease;
     min-width: 90px; /* Ensure minimum button width */
     text-align: center;
     flex-grow: 1; /* Allow buttons to grow if needed */
}

.save-button {
    background-color: #28a745; /* Green */
    color: white;
}

.save-button:hover:not(:disabled) {
    background-color: #218838;
}

.cancel-button {
    background-color: #6c757d; /* Gray */
    color: white;
}

.cancel-button:hover:not(:disabled) {
    background-color: #5a6268;
}

button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.6;
}


.table-actions {
    margin-bottom: 15px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.table-actions button {
    padding: 8px 12px;
    border: 1px solid #007bff;
    border-radius: 4px;
    background-color: #007bff;
    color: white;
    cursor: pointer;
    transition: background-color 0.3s ease, opacity 0.3s ease;
     flex-grow: 1; /* Allow buttons to grow */
     min-width: 60px; /* Minimum width for action buttons */
     text-align: center;
}

/* Specific button styles */
.table-actions button:nth-child(2) { /* Edit button */
    background-color: #ffc107;
    border-color: #ffc107;
    color: #212529;
}
.table-actions button:nth-child(2):hover:not(:disabled) {
    background-color: #e0a800;
}

.table-actions button:nth-child(3) { /* Delete button */
    background-color: #dc3545;
    border-color: #dc3545;
    color: white;
}
.table-actions button:nth-child(3):hover:not(:disabled) {
    background-color: #c82333;
}

.table-actions button:hover:not(:disabled) {
    background-color: #0056b3;
    border-color: #0056b3;
}


.site-table { /* Reused class name for styling consistency */
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    font-size: 0.95rem; /* Slightly larger table font */
    border: 1px solid #ddd;
}

.site-table th,
.site-table td {
    padding: 12px;
    border: 1px solid #ddd;
    text-align: left;
}

.site-table th {
    background-color: #e9ecef;
    font-weight: bold;
    color: #495057;
    text-transform: uppercase;
    font-size: 0.85rem;
}

 .site-table tbody tr:nth-child(even) {
     background-color: #f8f9fa; /* Stripe rows */
 }

.site-table tbody tr:hover {
    background-color: #e9ecef;
    cursor: pointer;
}

.site-table tbody tr.selected {
    background-color: #cfe2ff;
     outline: 2px solid #007bff; /* Use outline instead of border to avoid layout shifts */
     outline-offset: -2px; /* Pull outline inwards */
}

 /* Center text in empty cell */
 .site-table td[colspan="2"] {
     text-align: center;
     font-style: italic;
     color: #666;
 }


/* Responsive adjustments */
@media (max-width: 768px) {
    .container {
        flex-direction: column;
        gap: 15px;
    }

    .form-panel,
    .table-panel {
        margin-bottom: 15px;
        min-width: unset;
        flex: unset;
        width: 100%;
        padding: 15px;
    }

    h2 {
        margin-bottom: 15px;
        padding-bottom: 10px;
        font-size: 1.3rem;
    }

    .form-group {
        margin-bottom: 15px;
    }

    .form-actions {
        margin-top: 20px;
        flex-direction: column;
        gap: 8px;
    }

    .save-button, .cancel-button {
        width: 100%;
        padding: 10px;
         min-width: unset;
    }

    .table-actions {
        flex-direction: column;
        gap: 8px;
    }

    .table-actions button {
        width: 100%;
        padding: 10px;
         min-width: unset;
    }
}

/* Notification Styling */
.notification {
    position: fixed;
    bottom: 20px;
    left: 20px;
    padding: 15px 20px;
    border-radius: 4px;
    color: white;
    z-index: 1000;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
     opacity: 1; /* Default opacity */
     transition: opacity 0.5s ease-in-out; /* Ensure transition is here */
}

 .notification.fade-out {
     opacity: 0;
 }


.notification.success {
    background-color: #28a745; /* Green */
}

.notification.error {
    background-color: #dc3545; /* Red */
}

.notification.warning {
    background-color: #ffc107; /* Yellow */
    color: #212529;
}

.error-message {
    color: #dc3545;
    margin-bottom: 15px;
    padding: 10px;
    border: 1px solid #dc3545;
    background-color: #fff3f3;
    border-radius: 4px;
     font-size: 0.9rem;
}

</style>
